import React, { useContext, useEffect, useState }  from 'react'
import { View, StyleSheet, ScrollView, KeyboardAvoidingView, TouchableOpacity, Platform, Linking } from 'react-native'
import { Button, Text, Spinner, Modal, Icon, Toggle, Layout, Select, SelectItem, useTheme } from '@ui-kitten/components'
import Purchases from 'react-native-purchases'
import { UserContext } from '../../global/UserProvider'
import * as Sentry from "@sentry/react-native"
import { useTranslation } from 'react-i18next'
import { format } from 'date-fns'
import { PRICING_PLANS, getPlanInfo, planStatusKey } from '../../utils/pricingPlans'
import { useAtomValue } from 'jotai'
import { organizationAtom, organizationSubscriptionInfoAtom, userLocaleAtom } from '../../global/AtomsProvider'
import { HintView } from '../../components/HintView'
import { syncOrganizationSubscription } from '../../global/Firebase/organizationSubscription'
import { applyRevenueCatIdentity } from '../../utils/revenueCatIdentity'
import { api } from '../../global/API/apiClient'

const APIKeys = {
  apple: "appl_jlaQJYaeoluKxjaoiTHZRxcTJrR",
  google: "your_revenuecat_google_api_key",
};

export default function BillingScreen( { navigation, route } ) {
  const theme = useTheme()
  const { t } = useTranslation()
  const [context, setContext] = useContext(UserContext)
  const organizationSubscriptionInfo = useAtomValue(organizationSubscriptionInfoAtom)
  const organization = useAtomValue(organizationAtom)
  const userLocale = useAtomValue(userLocaleAtom)
  const [currentPricingPlan, setCurrentPricingPlan] = useState(getPlanInfo(organizationSubscriptionInfo?.subscriptionPlan))
  const [isLoading, setIsLoading] = useState(false)
  const [paymentScriptLoaded, setPaymentScriptLoaded] = useState(false)
  const [paddleSubscriptions, setPaddleSubscriptions] = useState()
  const [revenuecatSubscriptions, setRevenuecatSubscriptions] = useState()
  const [revenuecatSubscriptionsForWeb, setRevenuecatSubscriptionsForWeb] = useState()
  const [revenuecatUserInfo, setRevenuecatUserInfo] = useState()
  const [appStoreManagementURL, setAppStoreManagementURL] = useState()
  const [localizedPricingPlans, setLocalizedPricingPlans] = useState(PRICING_PLANS.map(item => ({...item, text: t('pricingPlans.' + item.id + 'Name'), description: t('pricingPlans.' + item.id + 'Description')})))
  const [showCancelSurvey, setShowCancelSurvey] = useState(false)
  const [selectedReason, setSelectedReason] = useState()
  const [cancelSubscriptionUrl, setCancelSubscriptionUrl] = useState()
  const [cancelSubscriptionType, setCancelSubscriptionType] = useState()

  const cancelReasons = [
    { id: 'too_expensive', text: t('billing.cancelReasonTooExpensive') },
    { id: 'missing_features', text: t('billing.cancelReasonMissingFeatures') },
    { id: 'not_using', text: t('billing.cancelReasonNotUsing') },
    { id: 'found_better', text: t('billing.cancelReasonFoundBetter') },
    { id: 'business_closing', text: t('billing.cancelReasonBusinessClosing') },
    { id: 'bugs', text: t('billing.cancelReasonBugs') },
    { id: 'otherPlan', text: t('billing.cancelReasonOtherPlan') },
    { id: 'other', text: t('billing.cancelReasonOther') }
  ]

  useEffect(() => {
    let script
    if (Platform.OS === 'web') {
      script = document.createElement('script');
      script.src = 'https://cdn.paddle.com/paddle/paddle.js'
      script.async = true;
      script.addEventListener('load', () => {
        const Paddle = window.Paddle;
        // Paddle.Environment.set('sandbox');
        Paddle.Setup({ 
          vendor: 159571,
          eventCallback: function(data) {
            if (data.event === 'Checkout.PaymentComplete') {
              // Check to ensure the payment has not been flagged for manual fraud review
              if (!data.eventData.flagged) {
                var checkoutId = data.eventData.checkout.id;
                Paddle.Order.details(checkoutId, function(data) {
                  if (data) {
                    // Order data, downloads, receipts etc... available within 'data' variable
                    console.log(data)
                    alert(t('billing.billingSuccessReload'))
                  } else {
                    // Order processing delay - order details cannot be retrieved at the moment
                    console.log('Order is being processed')
                  }
                });
              } else {
                // Payment has not been fully processed at the moment, so order details cannot be retrieved
                console.log('Transaction pending review');
              }
            } else if (data.event === 'Checkout.Complete') {
              alert(t('billing.billingSuccessReload'))
            }
          }
        });
        setPaymentScriptLoaded(true)
      })
      document.body.appendChild(script);
      getSubscriptionInfoFromRevenueCatForWeb()
    } else if (Platform.OS === 'ios') {
      // ASO-AN-001: App User ID remains the ORGANIZATION id (org-owned entitlement — see
      // utils/revenueCatIdentity.js for why re-keying it to a person would be a billing outage,
      // not a metrics change). `applyRevenueCatIdentity` configures on first use and uses `logIn`
      // when the device has moved to a different organization, which a second `configure` does not
      // do. Attributes are unchanged, so the RevenueCat dashboard keeps the fields it has today.
      // `getRevenueCatOfferings` MUST run after this promise settles, not beside it:
      // `applyRevenueCatIdentity` is async (it asks the SDK whether it is configured before it
      // configures), while `Purchases.getOfferings()` rejects with UninitializedPurchasesError
      // until `configure` has run. Called synchronously here it would lose that race on every
      // first mount of a process — the plan list would come back with no purchasable package and
      // the user could not subscribe at all. `addCustomerInfoUpdateListener` is safe before
      // configure (it only appends to a listener array), so it stays where it is.
      void applyRevenueCatIdentity({
        purchases: Purchases,
        apiKey: APIKeys.apple,
        organizationId: context.user.organizationId,
        attributes: {
          "userId" : context.user.uid,
          "organizationName" : organizationSubscriptionInfo?.name,
          "userEmail" : context.user.email,
          "orgCreatedDate": ((organization?.createdDate && organizationSubscriptionInfo?.createdDate?.toDate instanceof Function)
            ? format(organizationSubscriptionInfo.createdDate.toDate(), 'd-M-y')
            : ''),
          "lastBillingUpdate": (organizationSubscriptionInfo?.subscriptionLastUpdated?.toDate instanceof Function
            ? format(organizationSubscriptionInfo.subscriptionLastUpdated.toDate(), 'd-M-y')
            : 'new'),
        },
      }).then(getRevenueCatOfferings)
      Purchases.addCustomerInfoUpdateListener(info => {
        // handle any changes to purchaserInfo
        setAppStoreManagementURL(info.managementURL)
        setRevenuecatUserInfo(info)
        setRevenuecatSubscriptions(info.entitlements.active)
      })
    }

    getPaddleActiveSubscriptions()

    return () => {
      if (Platform.OS === 'web') {
        document.body.removeChild(script)
      } else if (Platform.OS === 'ios') {
        Purchases.removeCustomerInfoUpdateListener()
      }
    }
  }, [])

  const getSubscriptionInfoFromRevenueCatForWeb = async () => {
    try {
      const response = await fetch('https://api.revenuecat.com/v1/subscribers/' + context.user.organizationId, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': 'Bearer ' + APIKeys.apple
        }
      })
      const data = await response.json()
      let subscriptions = Object.entries(data?.subscriber?.subscriptions ).map(([key, value]) => ({...value, willRenew: new Date(value.expires_date) > new Date(),id: key}))
      setRevenuecatSubscriptionsForWeb(subscriptions)
    } catch (error) {
      console.error(error);
    }
  }

  const getRevenueCatOfferings = async () => {
    try {
      let offerings = await Purchases.getOfferings()
      setLocalizedPricingPlans(localizedPricingPlans.map(plan => {
        let newProduct = offerings.all.freemium0.availablePackages.find(product => product.identifier === plan.revenuecatPackageId);
        return { ...plan, revenuecatPackage: newProduct };
      }))
    } catch (e) {
      console.log('err', e)
    }
  }

  const pay = async (plan) => {
    if (Platform.OS === 'web') {
      const Paddle = window.Paddle
      Paddle.Checkout.open({ 
        product: plan.paddleId,
        title: plan.text,
        message: plan.description,
        locale: userLocale,
        email: context.user.email,
        passthrough: JSON.stringify({ organizationId: context.user.organizationId, userId: context.user.uid, orgName: organizationSubscriptionInfo?.name }),
      })
    } else if (Platform.OS === 'ios') {
      setIsLoading(true)
      try {
        const {customerInfo} = await Purchases.purchasePackage(plan.revenuecatPackage)
      } catch (error) {
        console.log(error)
        if (error.code !== 1) Sentry.captureException(error)
      }
      setIsLoading(false)
    }
  }

  const getPaddleActiveSubscriptions = async () => {
    let newSubscriptions = await context.db.collection('subscriptions').get()
    newSubscriptions = newSubscriptions.docs.map(doc => ({...doc.data(), id: doc.id, ref: doc.ref}))
    setPaddleSubscriptions(newSubscriptions)
  }

  const cancelPaddleSubscription = (link) => {
    const Paddle = window.Paddle
    if (!Paddle) {
      alert(t('billing.continueOnWeb'))
    } else {
      setCancelSubscriptionUrl(link)
      setCancelSubscriptionType('paddle')
      setShowCancelSurvey(true)
    }
  }

  const updatePaddleSubscription = (link) => {
    const Paddle = window.Paddle
    if (!Paddle) {
      alert(t('billing.continueOnWeb'))
    } else {
      Paddle.Checkout.open({
        override: link,
        locale: userLocale,
      });
    }
  }

  const cancelRevenuecatSubscription = (link) => {
    if (Platform.OS !== 'ios') {
      alert(t('billing.continueOnIos'))
      return
    } else {
      setCancelSubscriptionUrl(link)
      setCancelSubscriptionType('revenuecat')
      setShowCancelSurvey(true)
    }
  }

  const restorePurchases = async () => {
    setIsLoading(true)
    try {
      const customerInfo = await Purchases.restorePurchases()
      console.log('customerInfo', customerInfo)
      // check if we know about this subscription
      if (customerInfo?.entitlements?.active?.length > 0 && organizationSubscriptionInfo.status !== 'active') {
        // ATL-855: a FIFTH client subscription writer, missed by ATL-851 — and the worst of them.
        // `updateSubscription(ref, …)` resolves through `getFirestoreRef(ref, 'subscriptions')`,
        // and `getFirestoreRef` tests `item.id` BEFORE its "already a real Firestore ref" branch —
        // a `DocumentReference` has `.id`, so even in Firestore read mode this rebuilt the path as
        // `organizations/{org}/subscriptions/{orgId}`: a self-keyed junk document in the very
        // subcollection `getPaddleActiveSubscriptions` renders, and NOT the organization document
        // anyone reads for billing. (A 20-org sample of RevenueCat orgs in prod found 0 such docs,
        // so the path is rare — iOS-only and gated on active entitlements — but it has never once
        // done what it says.) It now posts the same server-authoritative intent as the paywall.
        const result = await syncOrganizationSubscription(organization)
        if (result?.plan && result.plan !== 'free') {
          setCurrentPricingPlan(getPlanInfo(result.plan))
        } else {
          alert(t('billing.noPurchases'))
        }
      }
    } catch (error) {
      console.log(error)
      if (error.code !== 1) Sentry.captureException(error)
    }
    setIsLoading(false)
  }

  const allowPurchase = (plan) => {
    if (plan.id === 'free') {
      return false
    }
    if (plan.id === currentPricingPlan.id && organizationSubscriptionInfo?.subscriptionStatus !== 'deleted') {
      return false
    }
    if (Platform.OS === 'android') {
      return false
    }
    if (Platform.OS === 'web') {
      if (!paymentScriptLoaded) {
        return false
      }
      // if (!organizationSubscriptionInfo?.subscriptionSource || organizationSubscriptionInfo?.subscriptionSource === 'paddle') {
      //   return true
      // }
    }
    if (Platform.OS === 'ios') {
      // if (organizationSubscriptionInfo?.subscriptionSource === 'revenuecat') {
      //   // happens through app store
      //   return false
      // }
    }
    return true
  }

  const handleCancelSubscription = async () => {
    if (!selectedReason) {
      alert(t('billing.selectCancelReason'))
      return
    }

    // Save cancel survey
    try {
      await context.db.collection('cancelSurveys').add({
        reason: selectedReason,
        organizationId: organizationSubscriptionInfo?.id,
        organizationName: organizationSubscriptionInfo?.name,
        plan: organizationSubscriptionInfo?.subscriptionPlan,
        date: new Date()
      })
      // Dual write to Supabase
      await api.post('/cancel-surveys', {
        reason: selectedReason,
        organizationId: organizationSubscriptionInfo?.id,
        organizationName: organizationSubscriptionInfo?.name,
        plan: organizationSubscriptionInfo?.subscriptionPlan,
      });
    } catch (error) {
      console.error('Error saving cancel survey:', error)
    }

    // Handle different subscription types
    if (cancelSubscriptionType === 'paddle') {
      // Proceed with Paddle cancellation
      const Paddle = window.Paddle
      Paddle.Checkout.open({
        override: cancelSubscriptionUrl,
        locale: userLocale,
      })
    } else if (cancelSubscriptionType === 'revenuecat') {
      // For RevenueCat, redirect to management URL
      Linking.openURL(cancelSubscriptionUrl)
    }
    
    setShowCancelSurvey(false)
  }

  const renderCancelSurveyModal = () => (
    <Modal
      visible={showCancelSurvey}
      backdropStyle={{backgroundColor: 'rgba(0, 0, 0, 0.5)'}}
      onBackdropPress={() => setShowCancelSurvey(false)}>
      <Layout style={{padding: 20, borderRadius: 5, minWidth: 300}}>
        <Text category='h6' style={{marginBottom: 15}}>
          {t('billing.cancelSurveyTitle')}
        </Text>
        <Text style={{marginBottom: 15}}>
          {t('billing.cancelSurveyText')}
        </Text>
        <Select
          style={{marginBottom: 15}}
          placeholder={t('billing.selectReason')}
          value={cancelReasons.find(r => r.id === selectedReason)?.text}
          onSelect={index => setSelectedReason(cancelReasons[index.row].id)}>
          {cancelReasons.map(reason => 
            <SelectItem key={reason.id} title={reason.text} />
          )}
        </Select>
        <Button onPress={handleCancelSubscription} status='danger'>
          {t('continue')}
        </Button>
        <Button 
          appearance='ghost' 
          onPress={() => setShowCancelSurvey(false)}
          style={{marginTop: 10}}>
          {t('cancel')}
        </Button>
      </Layout>
    </Modal>
  )

  return (
    <KeyboardAvoidingView style={{flex: 1}} behavior="height" keyboardVerticalOffset = {100}>
      <ScrollView style={styles.container} keyboardShouldPersistTaps='handled' keyboardDismissMode='on-drag'>
        <Text appearance='hint' style={{textAlign: 'center'}}>
          {organizationSubscriptionInfo?.id}
        </Text>
    
        <Text category='label' style={{marginVertical: 5}}>
          {t('billing.organizationName', {orgName: organizationSubscriptionInfo?.name})}
        </Text>
        <Text category='label' style={{marginVertical: 5}}>
          {t('billing.currentPlan', {currentPlan: currentPricingPlan.text})}
        </Text>
        <Text category='label' style={{marginVertical: 5}}>
          {t('billing.planStatus', {planStatus: t(planStatusKey(organizationSubscriptionInfo))})}
        </Text>
        <Text category='label' style={{marginVertical: 5}}>
          {t('billing.monthlySalesUSD', {monthlySalesUSD: organizationSubscriptionInfo?.currentMonthSalesUSD?.toFixed()})}
        </Text>
        { (paddleSubscriptions?.length > 0 || (revenuecatSubscriptions && Object.keys(revenuecatSubscriptions).length > 0) || revenuecatSubscriptionsForWeb) && (
          <>
            <Text category='label' style={styles.label}>
              { t('billing.subscriptions') }
            </Text>
            <View style={{flexDirection: 'row', flexWrap: 'wrap', flex: 1, justifyContent: 'space-between', }}>
              { revenuecatSubscriptions && Object.entries(revenuecatSubscriptions).map(([key, value]) => (
                <Layout key={key} style={[styles.pricingCard, {backgroundColor: theme['background-basic-color-1']}]}>
                  <Text style={styles.column}>{t('billing.id') + ': ' + key}</Text>
                  <Text style={styles.column}>{t('billing.plan') + ': ' + getPlanInfo(value.productIdentifier).text}</Text>
                  <Text style={styles.column}>{t('billing.status') + ': ' + (value.willRenew ? t('billing.active') : t('billing.deleted'))}</Text>
                  <Text style={styles.column}>{t('billing.purchaseDate') + ': ' + format(new Date(value.originalPurchaseDateMillis), 'd-MM-y')}</Text>
                  <Text style={styles.column}>{t('billing.paidTill') + ': ' + format(new Date(value.expirationDateMillis), 'd-MM-y')}</Text>
                  <Text style={styles.column}>{t('billing.sourceRevenuecat')}</Text>
                  {appStoreManagementURL && value.willRenew && (
                    <Button 
                      appearance='outline' 
                      status='danger' 
                      style={[{marginTop: 5}, styles.column]} 
                      onPress={() => cancelRevenuecatSubscription(appStoreManagementURL)}
                    >
                      {t('billing.cancelSubscription')}
                    </Button>
                  )}
                </Layout>
              ))}
              { revenuecatSubscriptionsForWeb && revenuecatSubscriptionsForWeb.map((value) => (
                <Layout key={value.id} style={[styles.pricingCard, {backgroundColor: theme['background-basic-color-1']}]}>
                  <Text style={styles.column}>{t('billing.plan') + ': ' + getPlanInfo(value.id).text}</Text>
                  <Text style={styles.column}>{t('billing.status') + ': ' + (value.willRenew ? t('billing.active') : t('billing.deleted'))}</Text>
                  <Text style={styles.column}>{t('billing.purchaseDate') + ': ' + format(new Date(value.original_purchase_date), 'd-MM-y')}</Text>
                  <Text style={styles.column}>{t('billing.paidTill') + ': ' + format(new Date(value.expires_date), 'd-MM-y')}</Text>
                  <Text style={styles.column}>{t('billing.sourceRevenuecat')}</Text>
                  {value.willRenew && (
                    <Button 
                      appearance='outline' 
                      status='danger' 
                      style={[{marginTop: 5}, styles.column]} 
                      onPress={() => cancelRevenuecatSubscription(value.management_url)}
                    >
                      {t('billing.cancelSubscription')}
                    </Button>
                  )}
                </Layout>
              ))}
              { paddleSubscriptions && paddleSubscriptions?.map((subscription, index) => (
                <Layout key={subscription.id} style={[styles.pricingCard, {backgroundColor: theme['background-basic-color-1']}]}>
                  <Text style={styles.column}>{t('billing.id') + ': ' + subscription.id}</Text>
                  <Text style={styles.column}>{t('billing.plan') + ': ' + getPlanInfo(subscription.subscription_plan_id).text}</Text>
                  <Text style={styles.column}>{t('billing.status') + ': ' + t('billing.' + (subscription.status || subscription.newStatus || subscription.oldStatus))}</Text>
                  <Text style={styles.column}>{t('billing.sourcePaddle')}</Text>
                  {subscription.status !== 'deleted' && (
                    <>
                      <Button style={styles.column} appearance='outline' status='danger' onPress={() => cancelPaddleSubscription(subscription.cancel_url)}>{t('billing.cancelSubscription')}</Button>
                      <Button style={styles.column} appearance='outline' status='info' onPress={() => updatePaddleSubscription(subscription.update_url)}>{t('billing.updatePaymentMethod')}</Button>
                    </>
                  )}
                  {/* {subscription.status === 'past_due' && (
                    <Button 
                      style={styles.column} 
                      appearance='outline'
                      status='info'
                      onPress={() => retryFailedPayment(subscription.id)}
                    >
                      {t('billing.retryPayment')}
                    </Button>
                  )} */}
                </Layout>
              ))}
            </View>
          </>
        )}
        
        { Platform.OS === 'web' && organization?.currency?.id === 'rub' && (
          <HintView
            text={t('billing.webHintRussia')}
            iconName='alert-circle-outline' style={{marginVertical: 10}} 
            action={() => Linking.openURL('https://atlas-app.notion.site/c773e64af87240edb4b328ce89d83a22?pvs=4')}
            callToAction={t('openLink')}
          />
        )}

        { Platform.OS === 'ios' && organization?.currency?.id === 'rub' && (
          <HintView
            text={t('billing.iosHintRussia')}
            iconName='alert-circle-outline' style={{marginVertical: 10}} 
            action={() => Linking.openURL('https://atlas-app.notion.site/c773e64af87240edb4b328ce89d83a22?pvs=4')}
            callToAction={t('openLink')}
          />
        )}

        { Platform.OS === 'android' && (
          <HintView
            text={t('billing.androidBillingDisabled')}
            iconName='alert-circle-outline' style={{marginVertical: 10}} 
            action={() => Linking.openURL('https://atlas-app.org')}
            callToAction={t('openLink')}
          />
        )}
        
        <View>
          <Text category='label' style={styles.label}>
            { t('billing.changePlan') }
          </Text>
          <View>
            { localizedPricingPlans.map((plan, index) => (
              <View key={plan.id} style={[styles.pricingCard, {backgroundColor: theme['background-basic-color-1']}]}>
                <Text category='h6' style={{marginBottom: 5}}>{plan.text}</Text>
                <Text style={{marginBottom: 5}}>{plan.description}</Text>
                { plan.id !== 'free' && (
                  <Text category='s1' style={{marginBottom: 10}}>{(plan.revenuecatPackage ? plan.revenuecatPackage?.product?.priceString : ('$' + plan.price.usd)) + t('billing.perMonth')}</Text>
                )}
                { isLoading ? (
                  <View style={styles.spinner}><Spinner /></View>
                ) : (
                  <Button disabled={!allowPurchase(plan)} onPress={() => pay(plan)} style={styles.payButton}>{t('billing.buy')}</Button>
                )}
              </View>
            ))}
          </View>
        </View>
        
        { Platform.OS === 'ios' && (
          <Button style={{marginTop: 10}} onPress={restorePurchases}>{t('billing.restorePurchases')}</Button>
        )}
        <View style={{height: 100}}></View>
      </ScrollView>
      {renderCancelSurveyModal()}
    </KeyboardAvoidingView>
  )
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    padding: 20,
  },

  label: {
    marginBottom: 8,
    marginTop: 16,
  },
  // submitButton: {
  //   marginTop: 16,
  // },
  // spinner: {
  //   marginTop: 15,
  //   alignItems: 'center'
  // },
  pricingCard: {
    // flex: 1,
    padding: 10,
    borderRadius: 5,
    alignItems: 'center', 
    marginHorizontal: 5,
    minWidth: 200,
    marginBottom: 10
  },
  payButton: {
    marginTop: 'auto'
  },
  column: {
    marginBottom: 5
  }
});